import objectivesService from "./objectives.service";
import {
  CheckInCreateDto,
  KeyResultCreateDto,
  KeyResultDto,
  KeyResultUpdateDto,
  ObjectiveCreateDto,
  ObjectiveDto,
  ObjectiveUpdateDto,
  PersonalObjectiveCreateDto,
} from "./objectives.types";
import { createThunkWithError } from "../featureHelpers/errorParser";

// Lists (the arg is returned too, so the slice can drop deleted objectives of that list)

// With the user's id: the store also holds personal goals others shared, and
// the slice must only replace the user's own
export const getPersonalObjectives = createThunkWithError<
  { userId: string | undefined; objectives: ObjectiveDto[] },
  void
>("objectives/getPersonal", async (_, { getState }) => ({
  userId: getState().user.userData.id,
  objectives: await objectivesService.getPersonalObjectives(),
}));

export const getAreaObjectives = createThunkWithError<
  { userId: string | undefined; objectives: ObjectiveDto[] },
  void
>("objectives/getAreas", async (_, { getState }) => ({
  userId: getState().user.userData.id,
  objectives: await objectivesService.getAreaObjectives(),
}));

export const getGroupObjectives = createThunkWithError<
  { groupId: string; objectives: ObjectiveDto[] },
  string
>("objectives/getGroup", async (groupId) => ({
  groupId,
  objectives: await objectivesService.getGroupObjectives(groupId),
}));

export const getOrganizationObjectives = createThunkWithError<
  { organizationId: string; objectives: ObjectiveDto[] },
  string
>("objectives/getOrganization", async (organizationId) => ({
  organizationId,
  objectives: await objectivesService.getOrganizationObjectives(organizationId),
}));

// Create

export const createPersonalObjective = createThunkWithError<
  ObjectiveDto,
  PersonalObjectiveCreateDto
>("objectives/createPersonal", (data) =>
  objectivesService.createPersonalObjective(data),
);

export const createGroupObjective = createThunkWithError<
  ObjectiveDto,
  { groupId: string; data: ObjectiveCreateDto }
>("objectives/createGroup", ({ groupId, data }) =>
  objectivesService.createGroupObjective(groupId, data),
);

export const createOrganizationObjective = createThunkWithError<
  ObjectiveDto,
  { organizationId: string; data: ObjectiveCreateDto }
>("objectives/createOrganization", ({ organizationId, data }) =>
  objectivesService.createOrganizationObjective(organizationId, data),
);

// Single objective

export const getObjective = createThunkWithError<ObjectiveDto, string>(
  "objectives/get",
  (objectiveId) => objectivesService.getObjective(objectiveId),
);

export const updateObjective = createThunkWithError<
  ObjectiveDto,
  { objectiveId: string; data: ObjectiveUpdateDto }
>("objectives/update", ({ objectiveId, data }) =>
  objectivesService.updateObjective(objectiveId, data),
);

// Returns the id after deletion
export const deleteObjective = createThunkWithError<string, string>(
  "objectives/delete",
  (objectiveId) => objectivesService.deleteObjective(objectiveId),
);

// Key results (return the objective id, so the slice finds the objective)

export const addKeyResult = createThunkWithError<
  { objectiveId: string; keyResult: KeyResultDto },
  { objectiveId: string; data: KeyResultCreateDto }
>("objectives/addKeyResult", async ({ objectiveId, data }) => ({
  objectiveId,
  keyResult: await objectivesService.addKeyResult(objectiveId, data),
}));

export const updateKeyResult = createThunkWithError<
  { objectiveId: string; keyResult: KeyResultDto },
  { objectiveId: string; keyResultId: string; data: KeyResultUpdateDto }
>("objectives/updateKeyResult", async ({ objectiveId, keyResultId, data }) => ({
  objectiveId,
  keyResult: await objectivesService.updateKeyResult(
    objectiveId,
    keyResultId,
    data,
  ),
}));

export const deleteKeyResult = createThunkWithError<
  { objectiveId: string; keyResultId: string },
  { objectiveId: string; keyResultId: string }
>("objectives/deleteKeyResult", async ({ objectiveId, keyResultId }) => {
  await objectivesService.deleteKeyResult(objectiveId, keyResultId);
  return { objectiveId, keyResultId };
});

// Check-ins (returns the updated objective)

export const createCheckIn = createThunkWithError<
  ObjectiveDto,
  { objectiveId: string; data: CheckInCreateDto }
>("objectives/createCheckIn", ({ objectiveId, data }) =>
  objectivesService.createCheckIn(objectiveId, data),
);

// Linked projects and tasks (return the updated objective; the arg tells the
// tasks slice which project/task changed)

export const linkProject = createThunkWithError<
  ObjectiveDto,
  { objectiveId: string; projectId: string; keyResultId?: string }
>("objectives/linkProject", ({ objectiveId, projectId, keyResultId }) =>
  objectivesService.linkProject(objectiveId, projectId, keyResultId),
);

export const unlinkProject = createThunkWithError<
  ObjectiveDto,
  { objectiveId: string; projectId: string }
>("objectives/unlinkProject", ({ objectiveId, projectId }) =>
  objectivesService.unlinkProject(objectiveId, projectId),
);

export const linkTask = createThunkWithError<
  ObjectiveDto,
  { objectiveId: string; taskId: string; keyResultId?: string }
>("objectives/linkTask", ({ objectiveId, taskId, keyResultId }) =>
  objectivesService.linkTask(objectiveId, taskId, keyResultId),
);

export const unlinkTask = createThunkWithError<
  ObjectiveDto,
  { objectiveId: string; taskId: string }
>("objectives/unlinkTask", ({ objectiveId, taskId }) =>
  objectivesService.unlinkTask(objectiveId, taskId),
);
